"use client";

import { sanitize } from "@/lib/sanitize";
import type { PaymentPageItem } from "@/lib/api/payment-page.schema";
import type { Dictionary } from "@/app/[lang]/dictionaries";
import type { Cart } from "@/hooks/use-cart";
import { Text } from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Minus, Plus } from "lucide-react";
import { isBelowMarketMinimum } from "@/lib/markets";
import type { Market } from "@/lib/api/checkout.schema";

type FormatPrice = (amount: number, currency: string) => string;

interface ProductListProps {
  items: PaymentPageItem[];
  cart: Cart;
  dictionary: Dictionary;
  /** Quotes an amount in the currency the customer is viewing prices in */
  formatPrice: FormatPrice;
  /** Market being previewed; items under its floor cannot be paid from it */
  market?: Market | null;
}

interface CartControlsProps {
  item: PaymentPageItem;
  cart: Cart;
  dictionary: Dictionary;
  market?: Market | null;
}

function CartControls({ item, cart, dictionary, market }: CartControlsProps) {
  const { paymentPage } = dictionary;
  const qtyInCart = cart.getItemQuantity(item.id);
  const tracksQuantity = item.track_quantity === true;
  const isOutOfStock = tracksQuantity && item.quantity <= 0;
  const maxReached = tracksQuantity && qtyInCart >= item.quantity;
  // Carts are single-currency: an item priced in another currency than what
  // is already in the cart cannot be added until the cart is emptied.
  const currencyMismatch = qtyInCart === 0 && !cart.acceptsItem(item);

  if (isOutOfStock) {
    return (
      <Text variant="muted" className="text-xs text-destructive">
        {paymentPage.outOfStock}
      </Text>
    );
  }

  if (currencyMismatch) {
    return (
      <Text variant="muted" className="max-w-28 text-right text-xs">
        {paymentPage.differentCurrency}
      </Text>
    );
  }

  // Cross-border floor: say so here rather than letting the payer find out at pay
  if (
    qtyInCart === 0 &&
    market &&
    isBelowMarketMinimum(market, { value: item.price, currency: item.currency })
  ) {
    return (
      <Text variant="muted" className="max-w-28 text-right text-xs">
        {paymentPage.notPayableFrom} {market.country}
      </Text>
    );
  }

  // Non-tracked items: toggle on/off (qty 1 only)
  if (!tracksQuantity) {
    return (
      <Button
        variant={qtyInCart > 0 ? "default" : "outline"}
        size="icon"
        className="size-9 shadow-none"
        onClick={(e) => {
          e.stopPropagation();
          if (qtyInCart > 0) {
            cart.updateQuantity(item.id, 0);
          } else {
            cart.addItem(item);
          }
        }}
        aria-label={qtyInCart > 0 ? `Remove ${item.name}` : `Add ${item.name}`}
      >
        {qtyInCart > 0 ? <Minus className="size-3.5" /> : <Plus className="size-3.5" />}
      </Button>
    );
  }

  // Tracked items: +/- stepper
  if (qtyInCart > 0) {
    return (
      <div
        className="flex items-center gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="outline"
          size="icon"
          className="size-9 shadow-none"
          onClick={() => cart.updateQuantity(item.id, qtyInCart - 1)}
          aria-label={`Decrease quantity of ${item.name}`}
        >
          <Minus className="size-3.5" />
        </Button>
        <Text variant="small" className="w-6 text-center font-medium">
          {qtyInCart}
        </Text>
        <Button
          variant="outline"
          size="icon"
          className="size-9 shadow-none"
          disabled={maxReached}
          onClick={() => cart.addItem(item)}
          aria-label={`Increase quantity of ${item.name}`}
        >
          <Plus className="size-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      className="h-9 text-xs shadow-none"
      onClick={(e) => {
        e.stopPropagation();
        cart.addItem(item);
      }}
    >
      <Plus className="size-3.5" />
      {paymentPage.addToCart}
    </Button>
  );
}

function ProductItem({
  item,
  cart,
  dictionary,
  formatPrice,
  market,
}: {
  item: PaymentPageItem;
  cart: Cart;
  dictionary: Dictionary;
  formatPrice: FormatPrice;
  market?: Market | null;
}) {
  const initials = item.name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <details
      className="group rounded-lg border bg-background px-3 transition-colors open:bg-muted/50"
    >
      <summary className="flex items-center gap-3 w-full py-2.5 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
        <Avatar className="size-14 shrink-0 rounded-md md:size-16">
          {item.image_url && (
            <AvatarImage src={item.image_url} alt={item.name} />
          )}
          <AvatarFallback className="rounded-md bg-muted text-xs">
            {initials}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1 flex flex-col items-start gap-0.5">
          <Text
            variant="small"
            className="min-w-0 text-left text-[15px] line-clamp-2"
          >
            {item.name}
          </Text>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <span>
              {formatPrice(item.price, item.currency)}
            </span>
            <span>&middot;</span>
            {item.track_quantity ? (
              item.quantity > 0 ? (
                <span>{item.quantity} {dictionary.paymentPage.inStock}</span>
              ) : (
                <span className="text-destructive">{dictionary.paymentPage.outOfStock}</span>
              )
            ) : (
              <span>{dictionary.paymentPage.available}</span>
            )}
          </div>
        </div>
        <div
          className="shrink-0"
          onClick={(e) => e.preventDefault()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <CartControls
            item={item}
            cart={cart}
            dictionary={dictionary}
            market={market}
          />
        </div>
      </summary>
      {item.description && (
        <div
          className="prose prose-sm prose-muted max-w-none text-pretty [&>p]:my-3 [&>div]:my-3 [&_a]:text-blue-600 [&_a]:underline prose-strong:text-foreground pb-3"
          dangerouslySetInnerHTML={{
            __html: sanitize(
              item.description.replace(
                /<p><br><\/p>/g,
                '<div class="h-2"></div>',
              ),
            ),
          }}
        />
      )}
    </details>
  );
}

export function ProductList({
  items,
  cart,
  dictionary,
  formatPrice,
  market,
}: ProductListProps) {
  const activeItems = items.filter((item) => item.is_active);

  if (activeItems.length === 0) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-2">
      {activeItems.map((item) => (
        <ProductItem
          key={item.id}
          item={item}
          cart={cart}
          dictionary={dictionary}
          formatPrice={formatPrice}
          market={market}
        />
      ))}
    </div>
  );
}
